/**
 * Ajido Warranty Web — Frontend state machine
 * Spec: ../../warranty-web-brainstorm/docs/specs/2026-05-15-warranty-web-design.md
 *
 * States: #lookup → #form → #success
 * 4 paths (matched / matched+mismatch / not_found / no_code)
 */

(function () {
  'use strict';

  const app = document.getElementById('warranty-app');
  if (!app) return;

  const API_BASE = app.dataset.apiBase || '/apps/warranty';

  // ===== State =====
  let currentState = {
    name: 'lookup',
    ref: null,
    refType: null,       // 'tracking_number' | 'shopee_order_code' | 'warranty_gen' | 'manual'
    lookupResult: null,  // response from /lookup
    warrantyId: null,
    step: 1,
  };

  // ===== Sections =====
  const sections = {
    lookup:  app.querySelector('[data-state="lookup"]'),
    result:  app.querySelector('[data-state="result"]'),
    form:    app.querySelector('[data-state="form"]'),
    success: app.querySelector('[data-state="success"]'),
  };

  const errorModal = document.getElementById('error-modal');
  const toast = document.getElementById('toast');

  // ===== Routing =====
  function setHash(hash) {
    if (location.hash !== hash) {
      history.pushState(null, '', hash || '#lookup');
    }
    routeToState();
  }

  function routeToState() {
    const hash = (location.hash || '#lookup').replace(/^#/, '').split('?')[0];
    const target = sections[hash] ? hash : 'lookup';

    for (const [name, el] of Object.entries(sections)) {
      el.hidden = (name !== target);
    }
    currentState.name = target;

    // Reset success state when navigating back
    if (target === 'lookup' && currentState.lookupResult) {
      currentState = { name: 'lookup', ref: null, refType: null, lookupResult: null, warrantyId: null, step: 1 };
      document.getElementById('ref-input').value = '';
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  window.addEventListener('hashchange', routeToState);
  routeToState();

  // ===== Lookup form =====
  const lookupForm = document.getElementById('lookup-form');
  const refInput = document.getElementById('ref-input');
  const noCodeLink = document.getElementById('no-code-link');

  lookupForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const ref = refInput.value.trim();
    if (!ref || ref.length < 3) {
      showHelperError(refInput, 'Vui lòng nhập mã đơn hoặc tracking number');
      return;
    }
    await doLookup(ref);
  });

  noCodeLink.addEventListener('click', (e) => {
    e.preventDefault();
    const wid = generateWarrantyId();
    currentState = { name: 'form', ref: wid, refType: 'warranty_gen', lookupResult: { status: 'no_code' }, warrantyId: wid, step: 1 };
    renderForm();
    setHash('#form');
  });

  async function doLookup(ref) {
    const btn = lookupForm.querySelector('.btn-primary');
    btn.classList.add('is-loading');
    btn.disabled = true;

    try {
      const res = await fetch(`${API_BASE}/lookup?ref=${encodeURIComponent(ref)}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });
      const data = await res.json();

      if (!res.ok) {
        showError({
          title: 'Có lỗi khi tra cứu',
          message: data?.error?.message_vi || 'Vui lòng thử lại sau ít phút.',
        });
        return;
      }

      // Handle blocked cases (modal)
      if (data.status === 'blocked') {
        const map = {
          already_activated: { title: 'Đơn đã được kích hoạt', icon: '🚫', csk: true },
          not_delivered_yet: { title: 'Đơn chưa được giao', icon: '📦', csk: false },
          no_products_in_policy: { title: 'SP không có chương trình bảo hành', icon: '⚠️', csk: true },
        };
        const info = map[data.blocked_reason] || { title: 'Không thể kích hoạt', icon: '⚠️', csk: true };
        showError({ title: info.title, message: data.message_vi, icon: info.icon, showCsk: info.csk });
        return;
      }

      // Path 3: not found → form with manual entry (no result view because no data)
      if (data.status === 'not_found') {
        currentState = {
          name: 'form', ref: ref, refType: 'manual',
          lookupResult: data, warrantyId: ref, step: 1
        };
        renderForm();
        setHash('#form');
        return;
      }

      // Path 1: found → SHOW RESULT VIEW (read-only) trước
      currentState = {
        name: 'result', ref: ref, refType: data.ref_type,
        lookupResult: data, warrantyId: data.warranty_id, step: 1
      };
      renderResult();
      setHash('#result');

    } catch (err) {
      console.error(err);
      showToast('Mất kết nối. Đang thử lại...');
      // Could implement retry here
    } finally {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  // ===== Render result (view-only kết quả tra cứu) =====
  const resultContent = document.getElementById('result-content');

  function renderResult() {
    const lr = currentState.lookupResult;
    if (!lr || !lr.order) return;
    const order = lr.order;
    const items = order.items || [];
    const maskedName = order.masked_name || '—';
    const maskedPhone = order.masked_phone || '—';
    const channelMap = { shopee: 'Shopee', tiktok_shop: 'TikTok Shop', sapo: 'Web/POS Sapo', manual: 'Thủ công' };
    const channelName = channelMap[order.channel] || order.channel || 'Đơn online';
    const deliveredDisplay = order.delivered_on ? formatDate(order.delivered_on) : '—';
    const province = order.province || '';

    let html = `<div class="ref-card">
      <div class="ref-label">Mã bảo hành</div>
      <div class="ref-value">${escapeHtml(currentState.warrantyId)}</div>
    </div>`;

    html += `<div class="info-panel">
      <div class="info-row"><span class="info-label">Kênh</span><span class="info-value">${escapeHtml(channelName)}</span></div>
      <div class="info-row"><span class="info-label">Ngày giao</span><span class="info-value">${deliveredDisplay}</span></div>
      <div class="info-row"><span class="info-label">Người nhận</span><span class="info-value mono">${escapeHtml(maskedName)}${province ? ' · ' + escapeHtml(province) : ''}</span></div>
      <div class="info-row"><span class="info-label">SĐT đăng ký</span><span class="info-value mono">${escapeHtml(maskedPhone)}</span></div>
    </div>`;

    html += `<div class="step-label" style="margin: 14px 0 8px;">${items.length} sản phẩm trong đơn</div>`;
    for (const item of items) {
      const expCls = item.expired ? ' expired' : '';
      const statusBadge = item.expired
        ? `<span class="badge badge-expired">Đã hết hạn</span>`
        : `<span class="badge badge-active">Còn hiệu lực</span>`;
      const warrantyLine = item.expired
        ? `<div class="product-meta expired">Đã hết bảo hành ngày ${formatDate(item.warranty_end)}</div>`
        : `<div class="product-meta">Bảo hành ${item.warranty_months} tháng · còn <strong>${item.days_remaining} ngày</strong> · đến <strong>${formatDate(item.warranty_end)}</strong></div>`;
      html += `<div class="product-card${expCls}">
        <div class="product-icon">📦</div>
        <div class="product-info">
          <div class="product-name">${escapeHtml(item.product_name)} ${statusBadge}</div>
          ${warrantyLine}
        </div>
      </div>`;
    }

    // CTA: kích hoạt
    const allExpired = items.length > 0 && items.every(i => i.expired);
    const ctaText = allExpired ? 'Lưu hồ sơ (đã hết hạn)' : 'Kích hoạt bảo hành cho đơn này';
    html += `<button type="button" id="result-activate-btn" class="btn-primary" style="margin-top: 16px;">${ctaText}</button>`;
    html += `<a href="#lookup" class="btn-link" id="result-back">← Tra cứu đơn khác</a>`;

    resultContent.innerHTML = html;

    document.getElementById('result-activate-btn').addEventListener('click', () => {
      currentState.step = 1;
      renderForm();
      setHash('#form');
    });
  }

  // ===== Render form (Step 1 + Step 2) =====
  const formContent = document.getElementById('form-content');
  const stepLabel = document.getElementById('step-label');

  function renderForm() {
    if (currentState.step === 1) {
      renderStep1();
    } else {
      renderStep2();
    }
  }

  function renderStep1() {
    setStepIndicator(1);
    stepLabel.textContent = 'Bước 1/2 · Thông tin bắt buộc';

    const lr = currentState.lookupResult;
    const path = (lr.status === 'found') ? 'matched' :
                 (currentState.refType === 'warranty_gen') ? 'no_code' : 'manual';

    let html = '';

    // Ref card
    const refCardClass = (path === 'no_code') ? 'gen' : '';
    const refLabel = (path === 'no_code') ? 'Mã bảo hành (cấp mới)' : 'Mã bảo hành';
    html += `<div class="ref-card ${refCardClass}">
      <div class="ref-label">${refLabel}</div>
      <div class="ref-value">${escapeHtml(currentState.warrantyId)}</div>
    </div>`;

    if (path === 'matched') {
      const order = lr.order;
      const items = order.items;
      const maskedName = order.masked_name || '';
      const maskedPhone = order.masked_phone || '';
      const phoneLast2 = (maskedPhone.match(/\d{2}$/) || [''])[0];

      // Tóm tắt nhẹ — chi tiết đã xem ở step Tra cứu
      const summaryProducts = items.length === 1
        ? items[0].product_name
        : `${items.length} sản phẩm`;
      html += `<div class="step-label" style="margin-bottom:14px;">Sắp kích hoạt: <strong>${escapeHtml(summaryProducts)}</strong></div>`;

      html += `
        <div class="field">
          <label class="field-label">
            <span>Họ tên</span>
            <span class="field-hint">tùy chọn · đăng ký: ${escapeHtml(maskedName)}</span>
          </label>
          <input type="text" id="full_name" placeholder="${escapeHtml(maskedName)}">
        </div>
        <div class="field">
          <label class="field-label">
            <span>Số điện thoại</span>
            <span class="field-hint">tùy chọn · đăng ký: ${escapeHtml(maskedPhone)}</span>
          </label>
          <input type="tel" id="phone" inputmode="numeric" maxlength="15" placeholder="0987654${phoneLast2}">
          <div class="input-meta">Để Ajido có thể liên hệ khi cần. Không bắt buộc.</div>
        </div>
      `;
    } else {
      // Path 3/4: manual entry
      if (path === 'manual') {
        html += `<div style="background:#fff3cd; border:1px solid #f4c453; border-radius:10px; padding:12px; font-size:12px; color:#5a4a00; line-height:1.5; margin-bottom:14px;">
          ⚠️ Không tìm thấy đơn này trong hệ thống. Có thể là đơn ngoài Ajido hoặc đơn cũ. Bạn có thể tự nhập hoặc <a href="https://zalo.me/1885621654350466464" target="_blank" rel="noopener" style="color:#FF6A00; text-decoration:underline; font-weight:700;">liên hệ Zalo OA</a>.
        </div>`;
      }
      html += `
        <div class="field">
          <label class="field-label">
            <span>Tên sản phẩm <span class="required">*</span></span>
            <span class="field-hint">tự gõ hoặc quét barcode</span>
          </label>
          <div class="input-wrap">
            <input type="text" id="product_name_input" list="model-list" placeholder="Ajido S5 PRO" required>
            <button type="button" class="qr-btn" id="barcode-scan-btn" aria-label="Quét barcode">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="5" width="18" height="14" rx="1"/>
                <line x1="6" y1="9" x2="6" y2="15"/>
                <line x1="9" y1="9" x2="9" y2="15"/>
                <line x1="12" y1="9" x2="12" y2="15"/>
                <line x1="15" y1="9" x2="15" y2="15"/>
                <line x1="18" y1="9" x2="18" y2="15"/>
              </svg>
            </button>
          </div>
          <datalist id="model-list">
            <option value="Ajido S5 PRO"><option value="Ajido S8"><option value="Ajido X8 ELITE">
            <option value="Ajido Mini Q5"><option value="Ajido Mini Q6"><option value="Ajido M9 Pro">
            <option value="Ajido H5 PRO"><option value="Ajido W05"><option value="Ajido INECK N3 PRO">
            <option value="Ajido SmartChef S1"><option value="Ajido Speedid X6"><option value="Ajido Scanbody S6">
            <option value="Ajido S1 Lite"><option value="Ajido Vaslist V6 Lite">
          </datalist>
          <div class="input-meta">Hệ thống auto-suggest từ 18 models · barcode scan support Chrome mobile</div>
        </div>
        <div class="field">
          <label class="field-label">
            <span>Ngày mua/nhận hàng <span class="required">*</span></span>
          </label>
          <input type="date" id="purchase_date" max="${todayStr()}" required>
        </div>
        <div class="field">
          <label class="field-label"><span>Họ tên</span><span class="field-hint">tùy chọn</span></label>
          <input type="text" id="full_name" placeholder="Nguyễn Văn A">
        </div>
        <div class="field">
          <label class="field-label"><span>Số điện thoại</span><span class="field-hint">tùy chọn</span></label>
          <input type="tel" id="phone" inputmode="numeric" maxlength="15" placeholder="0901234567">
        </div>
      `;
    }

    html += `<button type="button" id="step1-submit" class="btn-primary">Kích hoạt & tiếp tục</button>`;

    formContent.innerHTML = html;

    document.getElementById('step1-submit').addEventListener('click', () => submitStep1(path));
    const bcBtn = document.getElementById('barcode-scan-btn');
    if (bcBtn) bcBtn.addEventListener('click', startBarcodeScan);
  }

  async function submitStep1(path) {
    const btn = document.getElementById('step1-submit');
    const fullName = document.getElementById('full_name').value.trim();
    const phone = document.getElementById('phone').value.trim();

    // Customer fields là tùy chọn — không validate strict.
    // Chỉ check warranty_id (đã có từ lookup/gen). Path 3/4 cũng cần SP + ngày mua.

    // Build items
    let items = [];
    if (path === 'matched') {
      const deliveredISO = currentState.lookupResult.order.delivered_on || '';
      const deliveredDate = deliveredISO.slice(0, 10);  // chop ISO timestamp → YYYY-MM-DD
      items = currentState.lookupResult.order.items.map(it => ({
        product_code: it.product_code,
        purchase_date: deliveredDate,
      }));
    } else {
      const productName = document.getElementById('product_name_input').value.trim();
      const purchaseDate = document.getElementById('purchase_date').value;
      if (!productName || !purchaseDate) {
        showToast('Vui lòng chọn sản phẩm và ngày mua');
        return;
      }
      items = [{ product_name_input: productName, purchase_date: purchaseDate }];
    }

    btn.classList.add('is-loading');
    btn.disabled = true;

    try {
      const res = await fetch(`${API_BASE}/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          warranty_id: currentState.warrantyId,
          source_path: path,
          external_ref: currentState.ref,
          customer: { full_name: fullName, phone },
          items,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (data?.error?.code === 'ALREADY_ACTIVATED') {
          showError({ title: 'Đơn đã được kích hoạt', message: data.error.message_vi, showCsk: true });
        } else {
          showError({ title: 'Có lỗi', message: data?.error?.message_vi || 'Thử lại sau' });
        }
        return;
      }

      // Success — store + move to Step 2
      currentState.activateResult = data;
      currentState.customer = { full_name: fullName, phone };
      currentState.step = 2;
      renderStep2();

    } catch (err) {
      console.error(err);
      showToast('Mất kết nối. Vui lòng thử lại');
    } finally {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  function renderStep2() {
    setStepIndicator(2);
    stepLabel.textContent = 'Bước 2/2 · Hoàn thiện hồ sơ';

    const html = `
      <h1 style="font-size:18px; margin-bottom:6px;">Đã kích hoạt ✓</h1>
      <p class="lead">Bổ sung thông tin (không bắt buộc) để Ajido có thể chăm sóc tốt hơn</p>
      <div class="incentive-banner">
        <div class="incentive-icon">🎁</div>
        <div class="incentive-title">Voucher giảm 10% lần mua sau + quà sinh nhật</div>
        <div class="incentive-desc">Cho chúng tôi email + địa chỉ để gửi voucher và quà tặng. Không bắt buộc.</div>
      </div>
      <div class="field">
        <label class="field-label"><span>Email</span><span class="field-hint">không bắt buộc</span></label>
        <input type="email" id="step2_email" placeholder="ban@example.com">
      </div>
      <div class="field">
        <label class="field-label"><span>Địa chỉ chi tiết</span><span class="field-hint">không bắt buộc</span></label>
        <input type="text" id="step2_address" placeholder="Số nhà, đường, phường">
      </div>
      <div class="field">
        <label class="field-label"><span>Ngày sinh</span><span class="field-hint">để nhận quà sinh nhật</span></label>
        <input type="date" id="step2_dob" max="${todayStr()}">
      </div>
      <div class="checkbox-row">
        <input type="checkbox" id="step2_consent">
        <label for="step2_consent">Đồng ý nhận tin khuyến mãi & thông báo bảo hành qua email</label>
      </div>
      <button type="button" id="step2-submit" class="btn-primary">Hoàn tất</button>
      <a href="#" id="step2-skip" class="btn-link">Bỏ qua, đi đến kết quả →</a>
    `;

    formContent.innerHTML = html;

    document.getElementById('step2-submit').addEventListener('click', () => submitStep2(false));
    document.getElementById('step2-skip').addEventListener('click', (e) => {
      e.preventDefault();
      goToSuccess();
    });
  }

  async function submitStep2(skipOpt) {
    // v0.1: just go to success (Step 2 data update endpoint defer v0.2)
    // For now collect data and log; backend update endpoint can be added later
    if (!skipOpt) {
      const email = document.getElementById('step2_email').value.trim() || null;
      const address = document.getElementById('step2_address').value.trim() || null;
      const dob = document.getElementById('step2_dob').value || null;
      const consent = document.getElementById('step2_consent').checked;
      // TODO: PATCH /apps/warranty/profile to update customer_master
      console.log('Step 2 data (defer to v0.2):', { email, address, dob, consent });
    }
    goToSuccess();
  }

  function goToSuccess() {
    renderSuccess();
    setHash('#success');
  }

  function renderSuccess() {
    const ar = currentState.activateResult;
    const records = ar?.warranty_records || [];

    const allExpired = records.every(r => r.is_expired_at_activation);
    const successIcon = document.getElementById('success-icon');
    successIcon.className = 'check-circle' + (allExpired ? ' expired' : '');

    document.getElementById('success-title').textContent = allExpired ? 'Đã ghi nhận' : 'Đã kích hoạt!';
    document.getElementById('success-subtitle').textContent = allExpired
      ? 'Đơn này đã hết bảo hành, nhưng đã lưu vào hồ sơ của bạn'
      : `${records.length} sản phẩm trong đơn được bảo hành thành công`;
    document.getElementById('success-wid').textContent = currentState.warrantyId;

    const productsContainer = document.getElementById('success-products');
    let html = '';
    for (const r of records) {
      const expCls = r.is_expired_at_activation ? ' expired' : '';
      const meta = r.is_expired_at_activation
        ? `<div class="product-meta expired">Đã hết bảo hành</div>`
        : `<div class="product-meta">Bảo hành đến ${formatDate(r.warranty_end)} · còn ${r.days_remaining} ngày</div>`;
      html += `<div class="product-card">
        <div class="product-icon">📦</div>
        <div class="product-info">
          <div class="product-name">${escapeHtml(r.product_name || r.product_code)}</div>
          ${meta}
        </div>
      </div>`;
    }
    productsContainer.innerHTML = html;
  }

  // ===== Barcode scan (BarcodeDetector API) =====
  async function startBarcodeScan() {
    if (!('BarcodeDetector' in window)) {
      showToast('Trình duyệt không hỗ trợ quét barcode. Vui lòng nhập tay.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      // Simple modal video preview
      const modal = document.createElement('div');
      modal.style.cssText = 'position:fixed;inset:0;background:black;z-index:10000;display:flex;flex-direction:column;';
      const video = document.createElement('video');
      video.autoplay = true; video.playsInline = true;
      video.style.cssText = 'flex:1;width:100%;object-fit:cover;';
      video.srcObject = stream;
      const closeBtn = document.createElement('button');
      closeBtn.textContent = 'Đóng';
      closeBtn.style.cssText = 'padding:16px;background:#FF6A00;color:white;border:none;font-size:16px;font-weight:700;';
      modal.appendChild(video); modal.appendChild(closeBtn);
      document.body.appendChild(modal);

      const detector = new window.BarcodeDetector({ formats: ['ean_13', 'ean_8', 'code_128'] });
      const tick = async () => {
        if (!document.body.contains(modal)) return;
        try {
          const codes = await detector.detect(video);
          if (codes.length > 0) {
            stream.getTracks().forEach(t => t.stop());
            modal.remove();
            await resolveBarcodeToProduct(codes[0].rawValue);
            return;
          }
        } catch {/* ignore */}
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);

      closeBtn.addEventListener('click', () => {
        stream.getTracks().forEach(t => t.stop());
        modal.remove();
      });
    } catch (err) {
      console.error(err);
      showToast('Không thể truy cập camera');
    }
  }

  async function resolveBarcodeToProduct(barcode) {
    try {
      const res = await fetch(`${API_BASE}/barcode/${encodeURIComponent(barcode)}`);
      const data = await res.json();
      if (data.found) {
        const input = document.getElementById('product_name_input');
        if (input) input.value = data.product_name;
        showToast(`Đã nhận: ${data.product_name}`);
      } else {
        showToast(`Barcode ${barcode} không có trong hệ thống`);
      }
    } catch (err) {
      showToast('Lỗi tra cứu barcode');
    }
  }

  // ===== Error modal =====
  function showError({ title, message, icon = '⚠️', showCsk = false }) {
    document.getElementById('error-title').textContent = title;
    document.getElementById('error-message').textContent = message;
    document.getElementById('error-icon').textContent = icon;
    const cskBtn = document.getElementById('error-csk-btn');
    if (showCsk) {
      cskBtn.hidden = false;
      cskBtn.href = 'https://zalo.me/1885621654350466464';
    } else {
      cskBtn.hidden = true;
    }
    errorModal.hidden = false;
  }
  document.getElementById('error-close-btn').addEventListener('click', () => {
    errorModal.hidden = true;
  });

  // ===== Toast =====
  let toastTimer = null;
  function showToast(msg, ms = 3000) {
    toast.textContent = msg;
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toast.hidden = true; }, ms);
  }

  // ===== Helpers =====
  function setStepIndicator(n) {
    document.querySelectorAll('.step-dot').forEach((d, i) => {
      d.classList.toggle('active', i < n);
    });
  }

  function generateWarrantyId() {
    const year = new Date().getFullYear();
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let s = '';
    for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return `WAJ-${year}-${s}`;
  }

  function todayStr() {
    return new Date().toISOString().slice(0, 10);
  }

  function formatDate(iso) {
    if (!iso) return '';
    // Accept YYYY-MM-DD or ISO timestamp — chop tới date portion
    const dateOnly = String(iso).slice(0, 10);
    const d = dateOnly.split('-');
    if (d.length !== 3) return iso;
    return `${d[2]}/${d[1]}/${d[0]}`;
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  function showHelperError(input, msg) {
    input.classList.add('error');
    let helper = input.parentElement.parentElement.querySelector('.helper');
    if (!helper) {
      helper = document.createElement('p');
      helper.className = 'helper error';
      input.parentElement.parentElement.appendChild(helper);
    }
    helper.classList.add('error');
    helper.textContent = msg;
    setTimeout(() => {
      input.classList.remove('error');
      helper.classList.remove('error');
    }, 4000);
  }

  // ===== QR scan on lookup (reuse same as barcode) =====
  document.getElementById('qr-scan-btn').addEventListener('click', async () => {
    if (!('BarcodeDetector' in window)) {
      showToast('Vui lòng nhập tay (trình duyệt không hỗ trợ scan)');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      const modal = document.createElement('div');
      modal.style.cssText = 'position:fixed;inset:0;background:black;z-index:10000;display:flex;flex-direction:column;';
      const video = document.createElement('video');
      video.autoplay = true; video.playsInline = true; video.srcObject = stream;
      video.style.cssText = 'flex:1;width:100%;object-fit:cover;';
      const closeBtn = document.createElement('button');
      closeBtn.textContent = 'Đóng';
      closeBtn.style.cssText = 'padding:16px;background:#FF6A00;color:white;border:none;font-size:16px;font-weight:700;';
      modal.appendChild(video); modal.appendChild(closeBtn);
      document.body.appendChild(modal);
      const detector = new window.BarcodeDetector({ formats: ['qr_code', 'code_128', 'ean_13'] });
      const tick = async () => {
        if (!document.body.contains(modal)) return;
        try {
          const codes = await detector.detect(video);
          if (codes.length > 0) {
            stream.getTracks().forEach(t => t.stop());
            modal.remove();
            refInput.value = codes[0].rawValue;
            await doLookup(codes[0].rawValue);
            return;
          }
        } catch {/* */}
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      closeBtn.addEventListener('click', () => {
        stream.getTracks().forEach(t => t.stop());
        modal.remove();
      });
    } catch (err) {
      showToast('Không thể truy cập camera');
    }
  });

})();
